import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, type Manifest, type ManifestAsset } from '../shared/api.ts';
import { navigate, segmentAfter, usePath } from '../shared/router.ts';
import { formatBytes } from '../shared/format.ts';
import { ThemePicker } from './ThemePicker.tsx';
import { Lightbox } from './Lightbox.tsx';
import { justify, thumbnailFor, type LayoutRow } from './justified.ts';
import styles from './Gallery.module.css';

/** Target row heights behind the density control, in CSS pixels. */
const DENSITIES = [
  { key: 'dense', label: 'Dense', height: 180 },
  { key: 'standard', label: 'Standard', height: 300 },
  { key: 'large', label: 'Large', height: 460 },
] as const;

const GAP = 6;
const DENSITY_KEY = 'cq.density';

export function Gallery(): React.JSX.Element {
  const path = usePath();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [densityIndex, setDensityIndex] = useState(readDensity);
  const [width, setWidth] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  const openId = segmentAfter(path, '/g');

  useEffect(() => {
    api
      .get<Manifest>('/api/portfolio')
      .then(setManifest)
      .catch((caught: unknown) => {
        // An expired session is the common case, not an error worth showing:
        // send them back to the code entry.
        if (caught instanceof ApiError && caught.isUnauthenticated) {
          navigate('/', { replace: true });
          return;
        }
        setError(caught instanceof ApiError ? caught.message : 'Could not load this gallery.');
      });
  }, []);

  // ResizeObserver rather than a window resize listener, so the layout also
  // responds to a scrollbar appearing or the density control changing.
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry?.contentRect.width ?? 0);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [manifest]);

  const assets = manifest?.assets ?? [];
  const target = DENSITIES[densityIndex]?.height ?? 300;

  const rows = useMemo<LayoutRow[]>(
    () =>
      justify(
        assets.map((a) => ({ id: a.id, w: a.w, h: a.h })),
        { containerWidth: width, targetHeight: target, gap: GAP },
      ),
    [assets, width, target],
  );

  const byId = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);

  const setDensity = useCallback((index: number) => {
    setDensityIndex(index);
    try {
      localStorage.setItem(DENSITY_KEY, String(index));
    } catch {
      // Preference is per-view only; not worth surfacing.
    }
  }, []);

  if (error) {
    return (
      <main className={styles.message}>
        <p>{error}</p>
        <button className={styles.textButton} type="button" onClick={() => navigate('/')}>
          Enter a different code
        </button>
      </main>
    );
  }

  if (!manifest) return <main className={styles.message} aria-busy="true" />;

  // Deterministic, not array order: bundles finish building independently and
  // out of order, so `bundles[0]` could silently hand out the downscaled zip
  // in place of full-resolution originals. "Download all" means full quality.
  const bundle =
    manifest.portfolio.bundles.find((b) => b.size === 'original') ?? manifest.portfolio.bundles[0];

  return (
    <>
      <header className={`${styles.header} lit`}>
        <div className={styles.identity}>
          <span className={styles.title}>{manifest.portfolio.title}</span>
          {manifest.portfolio.clientName ? (
            <span className={styles.client}>{manifest.portfolio.clientName}</span>
          ) : null}
        </div>

        <div className={styles.controls}>
          <div className={styles.density} role="group" aria-label="Thumbnail size">
            {DENSITIES.map((density, index) => (
              <button
                key={density.key}
                type="button"
                className={styles.densityOption}
                aria-pressed={index === densityIndex}
                onClick={() => setDensity(index)}
              >
                {density.label}
              </button>
            ))}
          </div>

          {bundle ? (
            <a className={styles.download} href={bundle.path} download>
              Download all
              <span className={styles.bundleSize}>{formatBytes(bundle.bytes)}</span>
            </a>
          ) : null}

          <ThemePicker />

          <button className={styles.textButton} type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {manifest.portfolio.welcomeMessage ? (
        <p className={styles.welcome}>{manifest.portfolio.welcomeMessage}</p>
      ) : null}

      <main className={styles.grid} ref={gridRef}>
        {assets.length === 0 ? (
          <p className={styles.empty}>
            Your photos are still being prepared. Check back shortly.
          </p>
        ) : (
          rows.map((row, rowIndex) => (
            <div className={styles.row} key={rowIndex} style={{ gap: `${GAP}px` }}>
              {row.items.map((item) => {
                const asset = byId.get(item.id);
                if (!asset) return null;

                return (
                  <Tile
                    key={item.id}
                    asset={asset}
                    width={item.width}
                    height={item.height}
                    onOpen={() => navigate(`/g/${asset.id}`)}
                  />
                );
              })}
            </div>
          ))
        )}
      </main>

      {openId && byId.has(openId) ? (
        <Lightbox
          assets={assets}
          openId={openId}
          downloads={manifest.portfolio.downloads}
          onClose={() => navigate('/g')}
          onNavigate={(id) => navigate(`/g/${id}`, { replace: true })}
        />
      ) : null}
    </>
  );
}

interface TileProps {
  asset: ManifestAsset;
  width: number;
  height: number;
  onOpen: () => void;
}

function Tile({ asset, width, height, onOpen }: TileProps): React.JSX.Element {
  const variant = thumbnailFor(width, window.devicePixelRatio);
  const src = asset.variants[variant] ?? asset.variants['t900'] ?? asset.variants['t400'];

  return (
    <button
      type="button"
      className={styles.tile}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        // The placeholder is a 16px WebP inlined in the manifest, so the tile
        // shows the photograph's real colours before any image request lands.
        backgroundImage: asset.lqip ? `url(${asset.lqip})` : undefined,
      }}
      onClick={onOpen}
      aria-label={`Open ${asset.filename}`}
    >
      {src ? (
        <img className={styles.thumb} src={src} alt="" loading="lazy" decoding="async" />
      ) : null}

      {asset.type === 'video' ? (
        <span className={styles.playBadge} aria-hidden="true">
          &#9654;
        </span>
      ) : null}
    </button>
  );
}

function readDensity(): number {
  try {
    const stored = Number(localStorage.getItem(DENSITY_KEY));
    if (Number.isInteger(stored) && stored >= 0 && stored < DENSITIES.length) return stored;
  } catch {
    // Fall through to the default.
  }
  return 1;
}

async function signOut(): Promise<void> {
  try {
    await api.delete('/api/session');
  } finally {
    // Navigate regardless: the cookies may already have expired, and leaving
    // someone stuck on a dead gallery would be worse than a redundant hop.
    navigate('/');
  }
}
